import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar, Clock, CheckCircle2, Phone, MapPin, Users, 
  ChevronLeft, ChevronRight, Plus, Loader2, AlertCircle,
  CalendarCheck, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const eventTypeIcons = {
  follow_up: Phone,
  site_visit: MapPin,
  meeting: Users,
  call: Phone,
  reminder: Clock,
  other: Calendar
};

const eventTypeColors = {
  follow_up: 'bg-blue-100 text-blue-800 border-blue-200',
  site_visit: 'bg-green-100 text-green-800 border-green-200',
  meeting: 'bg-purple-100 text-purple-800 border-purple-200',
  call: 'bg-amber-100 text-amber-800 border-amber-200',
  reminder: 'bg-gray-100 text-gray-800 border-gray-200',
  other: 'bg-slate-100 text-slate-800 border-slate-200'
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // day, week, month
  const [filterType, setFilterType] = useState('all');
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    loadEvents();
  }, [selectedDate, viewMode]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Calculate date range based on view mode
      let startDate, endDate;
      if (viewMode === 'day') {
        startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);
      } else if (viewMode === 'week') {
        startDate = new Date(selectedDate);
        startDate.setDate(startDate.getDate() - startDate.getDay());
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
      } else {
        startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0, 23, 59, 59);
      }

      const res = await fetch(
        `${API}/api/calendar/events?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/calendar/events/${eventId}/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Event marked as completed');
        loadEvents();
        setShowEventModal(false);
      }
    } catch (error) {
      toast.error('Failed to complete event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/calendar/events/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Event deleted');
        loadEvents();
        setShowEventModal(false);
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const formatDateRange = () => {
    const options = { month: 'short', day: 'numeric' };
    if (viewMode === 'day') {
      return selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    } else if (viewMode === 'week') {
      const weekStart = new Date(selectedDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${weekStart.toLocaleDateString('en-US', options)} - ${weekEnd.toLocaleDateString('en-US', { ...options, year: 'numeric' })}`;
    } else {
      return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  const filteredEvents = filterType === 'all' 
    ? events 
    : events.filter(e => e.event_type === filterType);

  const groupEventsByDate = () => {
    const grouped = {};
    filteredEvents.forEach(event => {
      const dateKey = new Date(event.start_time).toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    return grouped;
  };

  const renderEventCard = (event) => {
    const Icon = eventTypeIcons[event.event_type] || Calendar;
    const colorClass = eventTypeColors[event.event_type] || eventTypeColors.other;
    const startTime = new Date(event.start_time);
    
    return (
      <Card 
        key={event.id} 
        className={`cursor-pointer hover:shadow-md transition-shadow ${event.is_completed ? 'opacity-60' : ''}`}
        onClick={() => {
          setSelectedEvent(event);
          setShowEventModal(true);
        }}
        data-testid={`event-${event.id}`}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${colorClass}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={`font-medium truncate ${event.is_completed ? 'line-through' : ''}`}>
                  {event.title}
                </p>
                {event.is_completed && (
                  <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
              </p>
              {event.lead && (
                <p className="text-sm text-muted-foreground mt-1">
                  Lead: {event.lead.name}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDayView = () => {
    const dayEvents = filteredEvents.filter(e => 
      new Date(e.start_time).toDateString() === selectedDate.toDateString()
    );

    return (
      <div className="space-y-3">
        {dayEvents.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CalendarCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No events scheduled for this day</p>
            </CardContent>
          </Card>
        ) : (
          dayEvents.map(renderEventCard)
        )}
      </div>
    );
  };

  const renderWeekView = () => {
    const grouped = groupEventsByDate();
    const weekStart = new Date(selectedDate);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      days.push(day);
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {days.map(day => {
          const dateKey = day.toDateString();
          const dayEvents = grouped[dateKey] || [];
          const isToday = day.toDateString() === new Date().toDateString();
          
          return (
            <div key={dateKey} className="space-y-2">
              <div className={`text-center p-2 rounded-lg ${isToday ? 'bg-amber-100' : 'bg-muted/50'}`}>
                <p className="text-xs text-muted-foreground">
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </p>
                <p className={`text-lg font-semibold ${isToday ? 'text-amber-700' : ''}`}>
                  {day.getDate()}
                </p>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {dayEvents.map(event => {
                  const Icon = eventTypeIcons[event.event_type] || Calendar;
                  const colorClass = eventTypeColors[event.event_type] || eventTypeColors.other;
                  
                  return (
                    <div 
                      key={event.id}
                      className={`p-2 rounded-lg text-xs cursor-pointer ${colorClass} ${event.is_completed ? 'opacity-60' : ''}`}
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowEventModal(true);
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <Icon className="h-3 w-3" />
                        <span className="truncate font-medium">{event.title}</span>
                      </div>
                      <p className="text-xs opacity-75">
                        {new Date(event.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMonthView = () => {
    const grouped = groupEventsByDate();
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    
    // Get first day of month and total days
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    // Create calendar grid (6 rows x 7 cols for safety)
    const calendarDays = [];
    
    // Add empty cells for days before the first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendarDays.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(year, month, day));
    }

    // Day names header
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <Card>
        <CardContent className="p-4">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (!day) {
                return <div key={`empty-${index}`} className="min-h-[80px] bg-muted/20 rounded-lg" />;
              }
              
              const dateKey = day.toDateString();
              const dayEvents = grouped[dateKey] || [];
              const isToday = day.toDateString() === new Date().toDateString();
              const isSelected = day.toDateString() === selectedDate.toDateString();
              
              return (
                <div 
                  key={dateKey}
                  className={`min-h-[80px] p-1 rounded-lg border cursor-pointer transition-colors
                    ${isToday ? 'bg-amber-50 border-amber-300' : 'bg-background border-border hover:bg-muted/30'}
                    ${isSelected ? 'ring-2 ring-amber-500' : ''}`}
                  onClick={() => {
                    setSelectedDate(day);
                    setViewMode('day');
                  }}
                  data-testid={`month-day-${day.getDate()}`}
                >
                  <div className={`text-sm font-medium mb-1 ${isToday ? 'text-amber-700' : ''}`}>
                    {day.getDate()}
                  </div>
                  
                  {/* Event indicators */}
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map(event => {
                      const colorClass = eventTypeColors[event.event_type] || eventTypeColors.other;
                      return (
                        <div 
                          key={event.id}
                          className={`text-[10px] px-1 py-0.5 rounded truncate ${colorClass} ${event.is_completed ? 'opacity-50' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(event);
                            setShowEventModal(true);
                          }}
                        >
                          {event.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-muted-foreground px-1">
                        +{dayEvents.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">My Calendar</h1>
            <p className="text-muted-foreground">Manage your follow-ups and scheduled events</p>
          </div>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Date Navigation */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" onClick={goToToday}>Today</Button>
                <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="font-medium ml-2">{formatDateRange()}</span>
              </div>

              {/* View Toggle & Filter */}
              <div className="flex items-center gap-2">
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="follow_up">Follow-ups</SelectItem>
                    <SelectItem value="site_visit">Site Visits</SelectItem>
                    <SelectItem value="meeting">Meetings</SelectItem>
                    <SelectItem value="call">Calls</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          viewMode === 'day' ? renderDayView() : 
          viewMode === 'week' ? renderWeekView() : 
          renderMonthView()
        )}

        {/* Upcoming Events Sidebar */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upcoming Events</CardTitle>
            <CardDescription>Next 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            {events.filter(e => !e.is_completed && new Date(e.start_time) >= new Date()).slice(0, 5).length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No upcoming events
              </p>
            ) : (
              <div className="space-y-2">
                {events
                  .filter(e => !e.is_completed && new Date(e.start_time) >= new Date())
                  .slice(0, 5)
                  .map(event => {
                    const Icon = eventTypeIcons[event.event_type] || Calendar;
                    return (
                      <div key={event.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{event.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(event.start_time).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event Detail Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && (
                <>
                  {React.createElement(eventTypeIcons[selectedEvent.event_type] || Calendar, { className: "h-5 w-5" })}
                  {selectedEvent.title}
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Date & Time</Label>
                  <p className="font-medium">
                    {new Date(selectedEvent.start_time).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <Badge className={eventTypeColors[selectedEvent.event_type]}>
                    {selectedEvent.event_type.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              {selectedEvent.description && (
                <div>
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-sm">{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.lead && (
                <div>
                  <Label className="text-muted-foreground">Related Lead</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline">{selectedEvent.lead.name}</Badge>
                    <span className="text-sm text-muted-foreground">{selectedEvent.lead.phone}</span>
                  </div>
                </div>
              )}

              {selectedEvent.is_completed && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Completed</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedEvent && !selectedEvent.is_completed && (
              <Button 
                variant="outline" 
                className="text-green-600"
                onClick={() => handleCompleteEvent(selectedEvent.id)}
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Mark Complete
              </Button>
            )}
            <Button 
              variant="destructive" 
              onClick={() => selectedEvent && handleDeleteEvent(selectedEvent.id)}
            >
              Delete
            </Button>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
